// src/screens/TaskScreen.js
// ✅ DROP-IN: Tabs start at the top (TeamScreen-style), no extra header junk,
// ✅ meta moved into a slim info bar, actions moved into the toolbar.
// ✅ NEW: Calendar tab added (TaskCalendar)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { LayoutList, Columns3, CalendarDays, AlertCircle, Loader2, RefreshCw, Plus, GanttChartSquare, Gauge } from "lucide-react";

import { auth, db } from "../firebase";

import TaskList from "../components/tasks/TaskList";
import TaskKanban from "../components/tasks/TaskKanban";
import TaskCalendar from "../components/tasks/TaskCalendar";
import TaskGantt from "../components/tasks/TaskGantt";
import TaskWorkload from "../components/tasks/TaskWorkload";
import TaskModal from "../components/tasks/TaskModal";

function safeRole(role) {
  const r = (role || "").toString().trim().toLowerCase();
  if (r === "owner") return "owner";
  if (r === "admin") return "admin";
  if (r === "enduser" || r === "end_user" || r === "end user") return "endUser";
  if (r === "customer") return "customer";
  return "endUser";
}

function isCustomer(me) {
  return safeRole(me?.role) === "customer";
}

function isStaff(me) {
  const r = safeRole(me?.role);
  return r === "owner" || r === "admin" || r === "endUser";
}

function isPermissionDenied(err) {
  const code = err?.code || "";
  const msg = (err?.message || "").toLowerCase();
  return code === "permission-denied" || msg.includes("permission") || msg.includes("denied");
}

const PENDING_TASK_MODAL_KEY = "supplyhawk:pendingTaskModalId";

export default function TaskScreen({ companyId, me }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [resolvedMe, setResolvedMe] = useState(me || null);
  const [loadingMe, setLoadingMe] = useState(!me);

  useEffect(() => {
    if (me) {
      setResolvedMe(me);
      setLoadingMe(false);
    }
  }, [me]);

  useEffect(() => {
    if (resolvedMe) return;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoadingMe(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) setResolvedMe({ uid: user.uid, id: snap.id, ...snap.data() });
        else setResolvedMe({ uid: user.uid });
      } catch (e) {
        console.error("[TaskScreen] failed to load user doc:", e);
      } finally {
        setLoadingMe(false);
      }
    });

    return () => unsub();
  }, [resolvedMe]);

  const cid = companyId || resolvedMe?.companyId;

  const [usersMap, setUsersMap] = useState({});
  const [accountsMap, setAccountsMap] = useState({});
  const [groupsMap, setGroupsMap] = useState({});

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? "kanban" : "list"
  ); // list | kanban | calendar | gantt | workload
  const [modalMode, setModalMode] = useState(null); // "create" | "edit"
  const [editTask, setEditTask] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const openingTaskIdRef = useRef(null);

  const canSeeTasks = useMemo(() => {
    if (!resolvedMe) return false;
    if (isCustomer(resolvedMe)) return false;
    return isStaff(resolvedMe);
  }, [resolvedMe]);

  const requestedTaskId = useMemo(() => {
    const fromState = (location.state?.openTaskId || "").toString().trim();
    if (fromState) return fromState;
    const params = new URLSearchParams(location.search || "");
    const fromUrl = (params.get("taskId") || "").trim();
    if (fromUrl) return fromUrl;
    if (typeof window === "undefined") return "";
    try {
      return (window.sessionStorage.getItem(PENDING_TASK_MODAL_KEY) || "").trim();
    } catch {
      return "";
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadMeta = async (companyIdToUse) => {
    if (!companyIdToUse) return;
    if (!resolvedMe) return;

    setLoadingMeta(true);
    setMetaError("");

    const role = safeRole(resolvedMe.role);
    const myUid = resolvedMe.uid || auth.currentUser?.uid || null;

    const baseUsersMap = {};
    if (myUid) {
      baseUsersMap[myUid] = {
        id: resolvedMe.id || myUid,
        uid: myUid,
        email: resolvedMe.email,
        displayName: resolvedMe.displayName || resolvedMe.fullName || resolvedMe.name,
        name: resolvedMe.fullName || resolvedMe.displayName || resolvedMe.name,
        ...resolvedMe,
      };
    }

    try {
      // Users: Admin/Owner can load company users; EndUser only sees self (rules-friendly).
      if (role === "owner" || role === "admin") {
        const usersQ = query(collection(db, "users"), where("companyId", "==", companyIdToUse));
        const usersSnap = await getDocs(usersQ);

        const uMap = { ...baseUsersMap };
        usersSnap.docs.forEach((d) => {
          const data = d.data();
          const key = data.uid || d.id;
          uMap[key] = { id: d.id, uid: data.uid || d.id, ...data };
        });

        if (myUid && !uMap[myUid]) uMap[myUid] = baseUsersMap[myUid];
        setUsersMap(uMap);
      } else {
        setUsersMap(baseUsersMap);
      }

      // Groups
      try {
        const groupsSnap = await getDocs(collection(db, "companies", companyIdToUse, "groups"));
        const gMap = {};
        groupsSnap.docs.forEach((d) => (gMap[d.id] = { id: d.id, ...d.data() }));
        setGroupsMap(gMap);
      } catch (e) {
        if (!isPermissionDenied(e)) console.error("[TaskScreen] groups load failed:", e);
        setGroupsMap({});
      }

      // Accounts
      try {
        const accountsSnap = await getDocs(collection(db, "companies", companyIdToUse, "accounts"));
        const aMap = {};
        accountsSnap.docs.forEach((d) => (aMap[d.id] = { id: d.id, ...d.data() }));
        setAccountsMap(aMap);
      } catch (e) {
        if (!isPermissionDenied(e)) console.error("[TaskScreen] accounts load failed:", e);
        setAccountsMap({});
      }

    } catch (e) {
      console.error("[TaskScreen] meta load failed:", e);
      setMetaError(e?.message || "Failed to load task metadata.");
    } finally {
      setLoadingMeta(false);
    }
  };

  useEffect(() => {
    if (cid && resolvedMe) loadMeta(cid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, resolvedMe?.uid]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setRefreshKey((p) => p + 1);
      if (cid && resolvedMe) await loadMeta(cid);
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenCreate = () => {
    setEditTask(null);
    setModalMode("create");
  };

  const handleOpenEdit = (task) => {
    setEditTask(task);
    setModalMode("edit");
  };

  const handleCloseModal = () => {
    setModalMode(null);
    setEditTask(null);

    if (typeof window !== "undefined") {
      try {
        window.sessionStorage.removeItem(PENDING_TASK_MODAL_KEY);
      } catch {}
    }

    const params = new URLSearchParams(location.search || "");
    if (params.has("taskId")) {
      params.delete("taskId");
      params.delete("commentId");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : "",
        },
        { replace: true }
      );
    }
  };

  const handleTaskSaved = () => {
    handleCloseModal();
    handleRefresh();
  };

  const tabs = [
    { id: "list", label: "All Tasks", Icon: LayoutList },
    { id: "kanban", label: "By Status", Icon: Columns3 },
    { id: "calendar", label: "Calendar", Icon: CalendarDays },
    { id: "gantt", label: "Gantt", Icon: GanttChartSquare },
    { id: "workload", label: "Workload", Icon: Gauge },
  ];
  const mobileActiveTab = ["list", "kanban"].includes(activeTab) ? activeTab : "kanban";

  useEffect(() => {
    if (isMobile && ["gantt", "workload", "calendar"].includes(activeTab)) {
      setActiveTab("kanban");
    }
  }, [activeTab, isMobile]);

  useEffect(() => {
    let cancelled = false;

    const openRequestedTask = async () => {
      if (!requestedTaskId || !cid || !resolvedMe || !canSeeTasks) return;
      if (loadingMe || loadingMeta) return;
      if (openingTaskIdRef.current === requestedTaskId) return;

      openingTaskIdRef.current = requestedTaskId;

      try {
        const snap = await getDoc(doc(db, "companies", cid, "tasks", requestedTaskId));
        if (!snap.exists()) return;

        if (cancelled) return;

        if (typeof window !== "undefined") {
          try {
            window.sessionStorage.removeItem(PENDING_TASK_MODAL_KEY);
          } catch {}
        }

        setActiveTab("list");
        setEditTask({ id: snap.id, ...snap.data() });
        setModalMode("edit");
      } catch (e) {
        console.error("[TaskScreen] failed to open requested task:", e);
      } finally {
        if (!cancelled) openingTaskIdRef.current = null;
      }
    };

    openRequestedTask();

    return () => {
      cancelled = true;
    };
  }, [requestedTaskId, cid, resolvedMe, canSeeTasks, loadingMe, loadingMeta]);

  // ----------------------------
  // Render guards
  // ----------------------------
  if (loadingMe) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-6">
        <div className="p-6 flex items-center gap-3 text-slate-700">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-medium">Loading user…</span>
        </div>
      </div>
    );
  }

  if (!resolvedMe) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-6">
        <div className="p-6 text-sm text-slate-700">Could not load user. Are you signed in?</div>
      </div>
    );
  }

  if (!cid) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-6">
        <div className="p-6 text-sm text-slate-700">
          Missing <code>companyId</code>.
        </div>
      </div>
    );
  }

  if (!canSeeTasks) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-6">
        <div className="p-6 text-sm text-slate-700">Your role does not have access to tasks.</div>
      </div>
    );
  }

  // ----------------------------
  // Main UI (TeamScreen-style)
  // ----------------------------
  return (
    <div className="bg-white overflow-hidden mb-6 md:rounded-2xl md:shadow-lg md:border md:border-slate-200">
      <div className="border-b border-slate-200 px-4 py-3 md:hidden">
        <nav className="inline-flex w-full rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
          <div className="grid w-full grid-cols-2 gap-1">
            {[
              { id: "kanban", label: "By Status", Icon: Columns3 },
              { id: "list", label: "List", Icon: LayoutList },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`flex items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-medium transition-all ${
                  mobileActiveTab === id
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
      {/* Top tabs row */}
      <div className="hidden md:block border-b border-slate-200">
        <div className="flex flex-wrap items-stretch justify-between gap-2">
          <nav className="flex flex-wrap">
            {tabs.map(({ id, label, Icon }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
                    active ? "text-blue-600 bg-blue-50" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                  {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
                </button>
              );
            })}
          </nav>

          {activeTab !== "calendar" ? (
            <div className="flex items-center gap-2 px-3 sm:px-6 py-3">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium disabled:opacity-60"
                title="Refresh"
              >
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Refresh
              </button>

              <button
                type="button"
                onClick={handleOpenCreate}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold"
                title="New Task"
              >
                <Plus className="w-4 h-4" />
                New Task
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className={activeTab === "calendar" ? "p-0" : "p-3 md:p-6"}>
        {activeTab !== "calendar" && metaError ? (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">{metaError}</div>
          </div>
        ) : null}

        {activeTab !== "calendar" && loadingMeta ? (
          <div className="mb-4 flex items-center gap-2 text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-xs font-medium">Loading metadata…</span>
          </div>
        ) : null}

        {activeTab === "list" ? (
          <TaskList
            key={refreshKey}
            companyId={cid}
            me={resolvedMe}
            usersMap={usersMap}
            groupsMap={groupsMap}
            onEdit={handleOpenEdit}
            onRefresh={handleRefresh}
            onCreateTask={handleOpenCreate}
            showManagerReport={isMobile}
          />
        ) : null}

        {activeTab === "kanban" ? (
          <TaskKanban
            key={refreshKey}
            companyId={cid}
            me={resolvedMe}
            usersMap={usersMap}
            groupsMap={groupsMap}
            onEdit={handleOpenEdit}
            onRefresh={handleRefresh}
            showManagerReport={!isMobile}
          />
        ) : null}

        {activeTab === "gantt" ? (
          <TaskGantt
            key={refreshKey}
            companyId={cid}
            me={resolvedMe}
            usersMap={usersMap}
            groupsMap={groupsMap}
            onEdit={handleOpenEdit}
            onRefresh={handleRefresh}
          />
        ) : null}

        {activeTab === "workload" ? (
          <TaskWorkload
            key={refreshKey}
            companyId={cid}
            me={resolvedMe}
            usersMap={usersMap}
            groupsMap={groupsMap}
            onEdit={handleOpenEdit}
            onRefresh={handleRefresh}
          />
        ) : null}

        {!isMobile && activeTab === "calendar" ? (
          <TaskCalendar
            key={refreshKey}
            refreshKey={refreshKey}
            companyId={cid}
            me={resolvedMe}
            usersMap={usersMap}
            accountsMap={accountsMap}
            groupsMap={groupsMap}
            onEdit={handleOpenEdit}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            onCreateTask={handleOpenCreate}
          />
        ) : null}
      </div>

      {modalMode ? (
        <TaskModal
          mode={modalMode}
          companyId={cid}
          me={resolvedMe}
          task={editTask}
          usersMap={usersMap}
          accountsMap={accountsMap}
          groupsMap={groupsMap}
          onClose={handleCloseModal}
          onSaved={handleTaskSaved}
        />
      ) : null}
    </div>
  );
}
